import { Users } from 'lucide-react';
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { EmptyState } from '../../../components/EmptyState.js';
import { useWorkfrontObjectCatalog } from '../hooks.js';

/**
 * Workfront · Resource management. Surfaces the "Resource"-typed
 * objects from the engine-detected catalog (Roles / Resources /
 * Resource Pools). Utilization tracking + assignment lands with the
 * customer-OAuth-credentialed Workfront API proxy in Stage 2 of the
 * workfront slice.
 */
const RESOURCE_TYPES = new Set(['ROLE', 'RSRC', 'RSRCPOOL', 'RESOURCE']);

interface ResourceRow {
  workfrontId: string;
  name: string;
  type: string;
  scenarioReferences: number;
  workfrontLink: string | null;
}

export function WorkfrontResourceManagementPage(): JSX.Element {
  const { data: rows = [], isLoading, error } = useWorkfrontObjectCatalog();

  const resources = useMemo<ReadonlyArray<ResourceRow>>(() => extractResources(rows), [rows]);

  return (
    <div className="relative z-10 flex h-full flex-col">
      <header className="flex flex-wrap items-center justify-between gap-3 px-6 pb-2 pt-6">
        <div>
          <h1 className="text-2xl font-light text-foreground">Workfront · Resources</h1>
          <p className="text-sm text-foreground/60">
            Roles, resources, and pools detected across this Workfront tenant. Utilization +
            assignment tracking ships with Stage 2.
          </p>
        </div>
        <Link
          to="/workfront"
          className="text-sm text-foreground/70 underline hover:text-foreground"
        >
          ← Object catalog
        </Link>
      </header>

      <div className="px-6 py-4">
        {error ? (
          <div className="rounded-md border border-rose-400/30 bg-rose-500/10 p-4 text-sm text-rose-800 dark:text-rose-200">
            Could not load Workfront objects: {error.message}
          </div>
        ) : isLoading ? (
          <div className="rounded-md border border-foreground/10 bg-foreground/[0.02] p-8 text-center text-sm text-foreground/50">
            Loading…
          </div>
        ) : resources.length === 0 ? (
          <EmptyState
            icon={Users}
            tint="sky"
            title="No roles or resources detected"
            description="Roles, resources, and resource pools surface here once the engine catalog picks them up from a Fusion scenario in this license."
          />
        ) : (
          <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {resources.map((r) => (
              <li
                key={r.workfrontId}
                className="flex items-center gap-3 rounded-md border border-foreground/10 bg-foreground/[0.03] p-3"
              >
                <div className="flex h-10 w-10 items-center justify-center rounded-md bg-sky-500/15">
                  <Users className="h-5 w-5 text-sky-700 dark:text-sky-300" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium text-foreground">{r.name}</div>
                  <div className="flex items-center gap-2 text-xs text-foreground/40">
                    <span className="uppercase tracking-wide">{r.type}</span>
                    <span>·</span>
                    <span>
                      {r.scenarioReferences} scenario{r.scenarioReferences === 1 ? '' : 's'}
                    </span>
                  </div>
                </div>
                {r.workfrontLink ? (
                  <a
                    href={r.workfrontLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-foreground/50 underline hover:text-foreground/80"
                  >
                    Open
                  </a>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function extractResources(
  rows: ReadonlyArray<Record<string, unknown>>,
): ReadonlyArray<ResourceRow> {
  return rows
    .filter((r) => {
      const t = ((r.workfrontType ?? '') as string).toUpperCase();
      return RESOURCE_TYPES.has(t);
    })
    .map((r) => ({
      workfrontId: typeof r.workfrontId === 'string' ? r.workfrontId : '',
      name:
        (typeof r.workfrontName === 'string' && r.workfrontName) ||
        (typeof r.workfrontId === 'string' ? r.workfrontId : '—'),
      type:
        (typeof r.workfrontTypeLabel === 'string' && r.workfrontTypeLabel) ||
        (typeof r.workfrontType === 'string' && r.workfrontType) ||
        '—',
      scenarioReferences: Array.isArray(r.fusionLinks) ? r.fusionLinks.length : 0,
      workfrontLink: typeof r.workfrontLink === 'string' ? r.workfrontLink : null,
    }))
    .sort((a, b) => b.scenarioReferences - a.scenarioReferences || a.name.localeCompare(b.name));
}
